import React, {useEffect, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
    Alert,
    Card,
    CardBody,
    Col,
    Collapse,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
    Row,
    TabContent,
    TabPane,
} from 'reactstrap';
import {useIntl} from 'react-intl';
import DataTable from '../../components/dataTable';
import {SelectField} from '../../components/react-select';
import Button from '../../components/button';
import I18nMessage from '../../components/I18nMessage';
import useAlerts from '../../hooks/use-alerts';
import useCustomers from '../../hooks/use-customers';
import useServices from '../../hooks/use-services';
import useStore from '../../hooks/use-store';
import Dialog from '../../autobahn/components/dialog';
import {getCurrentCustomerId} from '../../helpers/customer';
import {isCurrentProfileAdmin} from '../../helpers/profile';
import BillingBlockProvider, {useBillingBlock} from '../../autobahn/features/billing-block/provider';
import BillingBlockTab from '../../autobahn/features/billing-block/billing-block-tab';
import {
    buildChargePayload,
    buildListQueries,
    buildSubscriptionPayload,
    dateOnly,
    formatCurrency as formatCurrencyValue,
    formatDateValue as formatDate,
    whatsappPhone,
} from './helpers';
import history from '../../routes/history';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FiltersCard = styled(Card)`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: none;
  margin-bottom: 4px;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 991px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 575px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const IconActionButton = styled(Button)`
  min-width: 38px;
  min-height: 38px;
  padding-left: 0;
  padding-right: 0;
`;

// Elevation 3 of docs/design-system.md; the side bar marks only the status cards.
const accentColors = {green: '#00B106', yellow: '#FFC107', red: '#F23A2D'};

const SummaryCard = styled(Card)`
  border: 0;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(92, 99, 105, 0.16);
  overflow: hidden;
  position: relative;
  text-align: left;

  &::before {
    background: ${(props) => accentColors[props['data-accent']] || 'transparent'};
    content: '';
    inset: 0 auto 0 0;
    position: absolute;
    width: 4px;
  }

  .summary-label {
    color: #526172;
    display: block;
    font-size: 0.9rem;
    margin-bottom: 6px;
  }

  .summary-value {
    color: #14263f;
    display: block;
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.2;
  }

  .summary-count {
    color: #62748a;
    display: block;
    font-size: 0.85rem;
    margin-top: 4px;
  }
`;

const BillingBlockSettingsButton = ({onOpen}) => {
    const intl = useIntl();
    const {setSubtab} = useBillingBlock();
    const label = intl.formatMessage({id: 'billingBlockSettingsTitle'});
    return (
        <IconActionButton
            className="neutral"
            color="secondary"
            size="sm"
            title={label}
            aria-label={label}
            onClick={() => {
                onOpen();
                setSubtab('settings');
            }}
        >
            <i className="fas fa-cog" />
        </IconActionButton>
    );
};

BillingBlockSettingsButton.propTypes = {onOpen: PropTypes.func.isRequired};

const FiltersPanel = styled.div`
  text-align: left;
`;

const StatusLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const LegendItem = styled.span`
  align-items: center;
  color: #516173;
  display: inline-flex;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 8px;
`;

const LegendDot = styled.span`
  background: ${({color}) => color};
  border-radius: 999px;
  display: inline-block;
  height: 10px;
  width: 10px;
`;

const StatusPill = styled.span`
  align-items: center;
  background: ${({$background}) => $background};
  border: 1px solid ${({$border}) => $border};
  border-radius: 999px;
  color: ${({$color}) => $color};
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  min-height: 30px;
  padding: 0 12px;
  text-transform: uppercase;
`;

const ModalForm = styled(Form)`
  .modal-actions {
    border-top: 1px solid #e2e8f0;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
    padding-top: 20px;
  }
`;

const ModalDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const DetailGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
`;

const DetailCard = styled.div`
  background: #f8fbff;
  border: 1px solid #dde8f2;
  border-radius: 12px;
  padding: 10px 12px;

  .detail-label {
    color: #62748a;
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    margin-bottom: 4px;
  }

  .detail-value {
    color: #17314d;
    font-size: 0.88rem;
    font-weight: 500;
    line-height: 1.35;
    word-break: break-word;
  }
`;

const PaymentActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`;

const HelperText = styled.p`
  color: #516173;
  font-size: 0.84rem;
  margin: 0;
`;

const CodeBlock = styled.pre`
  background: #0f172a;
  border-radius: 12px;
  color: #e2e8f0;
  font-size: 0.78rem;
  margin: 0;
  overflow-x: auto;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const QrPreview = styled.div`
  align-items: center;
  background: linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%);
  border: 1px solid #dde8f2;
  border-radius: 14px;
  display: flex;
  justify-content: center;
  min-height: 180px;
  padding: 12px;

  img {
    background: #fff;
    border-radius: 10px;
    max-height: 210px;
    max-width: 100%;
    padding: 8px;
  }
`;

const CompactModal = styled(Modal)`
  .modal-dialog {
    max-width: 720px;
  }

  .modal-header {
    padding: 12px 16px;
  }

  .modal-title {
    color: #17314d;
    font-size: 1rem;
    font-weight: 600;
  }

  .modal-body {
    padding: 14px 16px 16px;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* One toolbar row per table: search (and filters) on the left, actions on the right; when the
     actions do not fit they wrap and stay right-aligned. */
  .react-bs-table-tool-bar > .row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px 0;
    justify-content: flex-end;
    margin: 0 0 12px;
  }

  .search-toolbar {
    margin: 0;
    order: 0;
  }

  .rounded-group-button {
    display: contents;
  }

  .rounded-group-button > * {
    order: 1;
  }

  .filter-toggle {
    margin-right: auto !important;
  }
`;

const SectionHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  h3 {
    color: #17314d;
    font-size: 0.92rem;
    font-weight: 600;
    margin: 0;
  }
`;

const TabShell = styled(Card)`
  border: 0;
  box-shadow: 0 14px 35px rgba(15, 23, 42, 0.08);
`;

const TabsHeader = styled(Nav)`
  border-bottom: 1px solid #e2e8f0;
  gap: 24px;
  padding: 0 20px;

  .nav-item {
    margin-bottom: -1px;
  }
`;

const TabsLink = styled(NavLink)`
  background: transparent;
  border: 0;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  color: #526172;
  cursor: pointer;
  font-weight: 600;
  padding: 14px 2px 11px;

  &:hover {
    color: #17314d;
  }

  &.active {
    border-bottom-color: #16A5DA;
    color: #17314d;
  }
`;

const defaultSummary = {
    totalCharges: 0,
    paidCharges: 0,
    unpaidCharges: 0,
    overdueCharges: 0,
    recurringCharges: 0,
    oneOffCharges: 0,
    totalChargeValue: 0,
    paidChargeValue: 0,
    unpaidChargeValue: 0,
    overdueChargeValue: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    inactiveSubscriptions: 0,
    activeSubscriptionValue: 0,
};

const defaultChargeForm = {
    customerId: '',
    billingType: 'PIX',
    value: '',
    dueDate: '',
    description: '',
    externalReference: '',
};

const defaultSubscriptionForm = {
    customerId: '',
    billingType: 'BOLETO',
    value: '',
    nextDueDate: '',
    cycle: 'MONTHLY',
    description: '',
    externalReference: '',
    endDate: '',
    maxPayments: '',
};

const noop = () => {};

const providerIssueMessageId = {
    MISSING_TOKEN: 'billingProviderMissingToken',
    MULTIPLE_ACTIVE_CONFIGS: 'billingProviderMultipleConfigs',
};

const chargeStatusPalette = {
    PENDING: {
        color: '#8a5a00',
        background: '#fff4cf',
        border: '#ffd166',
    },
    OVERDUE: {
        color: '#9f1239',
        background: '#ffe0e7',
        border: '#fda4af',
    },
    RECEIVED: {
        color: '#166534',
        background: '#dcfce7',
        border: '#86efac',
    },
    CONFIRMED: {
        color: '#0f766e',
        background: '#ccfbf1',
        border: '#5eead4',
    },
    RECEIVED_IN_CASH: {
        color: '#1d4ed8',
        background: '#dbeafe',
        border: '#93c5fd',
    },
};

const defaultStatusPalette = {
    color: '#475569',
    background: '#e2e8f0',
    border: '#cbd5e1',
};

const statusMessageId = (kind, status) => {
    if (!status) return 'notAvailable';
    const normalized = String(status).trim().toUpperCase();
    return `${kind}Status${normalized
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('')}`;
};

const cycleMessageId = (cycle) => {
    if (!cycle) return 'notAvailable';
    const normalized = String(cycle).trim().toUpperCase();
    return `billingCycle${normalized
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('')}`;
};

const resolveMessage = (intl, id, fallback) => {
    if (intl.messages && Object.prototype.hasOwnProperty.call(intl.messages, id)) {
        return intl.formatMessage({id});
    }
    return fallback || intl.formatMessage({id: 'notAvailable'});
};

const normalizeQrCodeImage = (value) => {
    if (!value) {
        return null;
    }
    return value.startsWith('data:') ? value : `data:image/png;base64,${value}`;
};

const BillingContainer = () => {
    const intl = useIntl();
    const {toast} = useAlerts();
    const {billingService} = useServices();
    const {customers: customerStore} = useCustomers({all: true});
    const currentCustomerId = getCurrentCustomerId();
    const {store} = useStore();
    // ponytail: only user.readonly; add server.readonly if a server is ever configured that way
    const readonly = !!store.auth.user?.readonly;

    const [filters, setFilters] = useState({
        provider: 'asaas',
        customerId: '',
        paid: '',
        overdue: '',
        recurring: '',
        active: '',
    });
    const [summary, setSummary] = useState(defaultSummary);
    const [charges, setCharges] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submittingCharge, setSubmittingCharge] = useState(false);
    const [submittingSubscription, setSubmittingSubscription] = useState(false);
    const [syncingCharge, setSyncingCharge] = useState(false);
    const [syncingSubscription, setSyncingSubscription] = useState(false);
    const [chargeForm, setChargeForm] = useState(defaultChargeForm);
    const [subscriptionForm, setSubscriptionForm] = useState(defaultSubscriptionForm);
    const [selectedCharge, setSelectedCharge] = useState(null);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [chargeModalOpen, setChargeModalOpen] = useState(false);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [paymentDetailsModalOpen, setPaymentDetailsModalOpen] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [loadingPaymentDetails, setLoadingPaymentDetails] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('charges');
    const billingBlock = useRef(null);
    // Only a central's own users configure blocking: the server would save it for any customer.
    const showBillingBlockTab = !!customerStore.isCurrentCustomerMonitoring && !isCurrentProfileAdmin();
    const changeTab = (tab) => {
        if (tab !== activeTab) billingBlock.current.confirmDiscard(() => setActiveTab(tab));
    };
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [providerStatus, setProviderStatus] = useState(null);
    const providerNotConfigured = providerStatus?.configured === false;

    const customerNameById = useMemo(
        () => new Map(customerStore.list.map((customer) => [customer.id, customer.name])),
        [customerStore.list],
    );

    const customerById = useMemo(
        () => new Map(customerStore.list.map((customer) => [customer.id, customer])),
        [customerStore.list],
    );

    const customerOptions = useMemo(() => customerStore.list
        .filter((customer) => customer.id !== currentCustomerId)
        .map((customer) => ({
            id: customer.id,
            name: customer.name,
        })), [customerStore.list, currentCustomerId]);

    const formatCurrency = (value) => formatCurrencyValue(intl, value || 0);
    const formatDateValue = (value) => formatDate(intl, value);

    const showError = (error) => toast.errorStatus(error, {legacy: false});

    const loadData = async (nextFilters = filters) => {
        setLoading(true);
        try {
            const queries = buildListQueries(nextFilters, currentCustomerId);
            const [nextSummary, nextCharges, nextSubscriptions] = await Promise.all([
                billingService.getSummary(queries.summary),
                billingService.listCharges(queries.charges),
                billingService.listSubscriptions(queries.subscriptions),
            ]);
            setSummary({...defaultSummary, ...nextSummary});
            setCharges(nextCharges || []);
            setSubscriptions(nextSubscriptions || []);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // A failed status check must not block the screen; submit still reports the backend error.
        billingService.getProviderStatus({provider: filters.provider, ownerCustomerId: currentCustomerId})
            .then(setProviderStatus)
            .catch(() => setProviderStatus(null));
    }, []);

    useEffect(() => {
        if (selectedCharge && !charges.some((charge) => charge.id === selectedCharge.id)) {
            setSelectedCharge(null);
            setPaymentDetails(null);
        }
    }, [charges, selectedCharge]);

    useEffect(() => {
        if (selectedSubscription && !subscriptions.some((subscription) => subscription.id === selectedSubscription.id)) {
            setSelectedSubscription(null);
        }
    }, [subscriptions, selectedSubscription]);

    // Adapts the change handlers written for native inputs to SelectField, which hands over the value.
    const fromValue = (handler) => (value) => handler({target: {value}});

    const updateFiltersField = (field) => (event) => {
        const {value} = event.target;
        setFilters((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const updateChargeField = (field) => (event) => {
        const {value} = event.target;
        setChargeForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const updateSubscriptionField = (field) => (event) => {
        const {value} = event.target;
        setSubscriptionForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const submitCharge = async (event) => {
        event.preventDefault();
        setSubmittingCharge(true);
        try {
            await billingService.createCharge(buildChargePayload(chargeForm, currentCustomerId, filters.provider));
            setChargeForm(defaultChargeForm);
            setChargeModalOpen(false);
            await loadData();
            toast.success(intl.formatMessage({id: 'billingChargeCreatedSuccess'}));
        } catch (error) {
            showError(error);
        } finally {
            setSubmittingCharge(false);
        }
    };

    const submitSubscription = async (event) => {
        event.preventDefault();
        setSubmittingSubscription(true);
        try {
            await billingService.createSubscription(buildSubscriptionPayload(subscriptionForm, currentCustomerId, filters.provider));
            setSubscriptionForm(defaultSubscriptionForm);
            setSubscriptionModalOpen(false);
            await loadData();
            toast.success(intl.formatMessage({id: 'billingSubscriptionCreatedSuccess'}));
        } catch (error) {
            showError(error);
        } finally {
            setSubmittingSubscription(false);
        }
    };

    const reuseChargeAsDraft = () => {
        if (!selectedCharge) {
            return;
        }
        setChargeForm({
            customerId: String(selectedCharge.customerId || ''),
            billingType: selectedCharge.billingType || 'PIX',
            value: selectedCharge.value != null ? String(selectedCharge.value) : '',
            dueDate: selectedCharge.dueDate ? dateOnly(selectedCharge.dueDate).format('YYYY-MM-DD') : '',
            description: selectedCharge.description || '',
            externalReference: '',
        });
        setChargeModalOpen(true);
    };

    const openInvoice = () => {
        if (!selectedCharge?.invoiceUrl) {
            return;
        }
        window.open(selectedCharge.invoiceUrl, '_blank', 'noopener,noreferrer');
    };

    const copyToClipboard = async (value, successMessageId) => {
        if (!value) {
            return false;
        }
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(value);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = value;
                textArea.setAttribute('readonly', 'readonly');
                textArea.style.position = 'absolute';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            toast.success(intl.formatMessage({id: successMessageId}));
            return true;
        } catch (error) {
            toast.error(intl.formatMessage({id: 'billingPaymentDetailsUnavailable'}));
            return false;
        }
    };

    const fetchChargePaymentDetails = async (charge = selectedCharge) => {
        if (!charge) {
            return null;
        }
        const details = await billingService.getChargePaymentDetails(charge.id, {ownerCustomerId: currentCustomerId});
        setPaymentDetails(details);
        await loadData();
        return details;
    };

    const openPaymentDetails = async () => {
        if (!selectedCharge) {
            return;
        }
        setPaymentDetailsModalOpen(true);
        setLoadingPaymentDetails(true);
        try {
            await fetchChargePaymentDetails(selectedCharge);
        } catch (error) {
            setPaymentDetails(null);
            showError(error);
        } finally {
            setLoadingPaymentDetails(false);
        }
    };

    const copyPaymentLink = async () => {
        if (!selectedCharge) {
            return;
        }
        try {
            const details = paymentDetails?.chargeId === selectedCharge.id ?
                paymentDetails :
                await fetchChargePaymentDetails(selectedCharge);
            const link = details?.paymentUrl || details?.bankSlipUrl || selectedCharge.invoiceUrl;
            if (!link) {
                toast.warn(intl.formatMessage({id: 'billingNoPaymentLink'}));
                return;
            }
            await copyToClipboard(link, 'billingPaymentLinkCopied');
        } catch (error) {
            showError(error);
        }
    };

    const copyPixCode = async () => {
        const pixCode = paymentDetails?.pixCopyPaste;
        if (!pixCode) {
            toast.warn(intl.formatMessage({id: 'billingNoPixQrCode'}));
            return;
        }
        await copyToClipboard(pixCode, 'billingPixCodeCopied');
    };

    const syncSelectedCharge = async () => {
        if (!selectedCharge) {
            return;
        }
        setSyncingCharge(true);
        try {
            await billingService.syncCharge(selectedCharge.id, {ownerCustomerId: currentCustomerId});
            await loadData();
            toast.success(intl.formatMessage({id: 'billingChargeSyncSuccess'}));
        } catch (error) {
            showError(error);
        } finally {
            setSyncingCharge(false);
        }
    };

    const cancelSelectedSubscription = async () => {
        setCancelDialogOpen(false);
        if (!selectedSubscription) {
            return;
        }
        setSubmittingSubscription(true);
        try {
            await billingService.cancelSubscription(selectedSubscription.id, {ownerCustomerId: currentCustomerId});
            await loadData();
            toast.success(intl.formatMessage({id: 'billingSubscriptionCancelledSuccess'}));
        } catch (error) {
            showError(error);
        } finally {
            setSubmittingSubscription(false);
        }
    };

    const syncSelectedSubscription = async () => {
        if (!selectedSubscription) {
            return;
        }
        setSyncingSubscription(true);
        try {
            await billingService.syncSubscription(selectedSubscription.id, {ownerCustomerId: currentCustomerId});
            await loadData();
            toast.success(intl.formatMessage({id: 'billingSubscriptionSyncSuccess'}));
        } catch (error) {
            showError(error);
        } finally {
            setSyncingSubscription(false);
        }
    };

    const mappedCharges = useMemo(() => [...charges]
        .sort((a, b) => String(b.dueDate || '').localeCompare(String(a.dueDate || '')))
        .map((charge) => ({
        ...charge,
        targetCustomerName: customerNameById.get(charge.customerId) || `#${charge.customerId || 0}`,
        recurrenceLabel: charge.billingSubscriptionId > 0 ?
            intl.formatMessage({id: 'billingRecurring'}) :
            intl.formatMessage({id: 'billingOneOff'}),
        statusLabel: resolveMessage(intl, statusMessageId('billingCharge', charge.status), charge.status),
        statusTone: chargeStatusPalette[String(charge.status || '').trim().toUpperCase()] || defaultStatusPalette,
        valueLabel: formatCurrency(charge.value),
        dueDateLabel: formatDateValue(charge.dueDate),
        paymentDateLabel: formatDateValue(charge.paymentDate),
    })), [charges, customerNameById, intl]);

    const mappedSubscriptions = useMemo(() => subscriptions.map((subscription) => ({
        ...subscription,
        targetCustomerName: customerNameById.get(subscription.customerId) || `#${subscription.customerId || 0}`,
        statusLabel: resolveMessage(intl, statusMessageId('billingSubscription', subscription.status), subscription.status),
        cycleLabel: resolveMessage(intl, cycleMessageId(subscription.cycle), subscription.cycle),
        valueLabel: formatCurrency(subscription.value),
        nextDueDateLabel: formatDateValue(subscription.nextDueDate),
    })), [subscriptions, customerNameById, intl]);

    const chargeCount = (count) => intl.formatMessage({id: 'billingSummaryChargeCount'}, {count: Number(count || 0)});
    const subscriptionCount = (count) => intl.formatMessage({id: 'billingSummarySubscriptionCount'}, {count: Number(count || 0)});
    // The server suspends a late client by setting status "frozen"; the status can also be set by hand.
    const suspendedCustomers = customerStore.list.filter((customer) => (
        customer.customerId === currentCustomerId && customer.category === 'client' && customer.status === 'frozen'
    )).length;
    const summaryCards = [
        {id: 'billingSummaryPaidCharges', value: formatCurrency(summary.paidChargeValue), count: chargeCount(summary.paidCharges), accent: 'green'},
        {id: 'billingSummaryUnpaidCharges', value: formatCurrency(summary.unpaidChargeValue), count: chargeCount(summary.unpaidCharges), accent: 'yellow'},
        {id: 'billingSummaryOverdueCharges', value: formatCurrency(summary.overdueChargeValue), count: chargeCount(summary.overdueCharges), accent: 'red'},
        {id: 'billingSummaryActiveSubscriptions', value: formatCurrency(summary.activeSubscriptionValue), count: subscriptionCount(summary.activeSubscriptions)},
        {id: 'billingSummaryTotalCharges', value: summary.totalCharges},
        {id: 'billingSummarySuspendedCustomers', value: suspendedCustomers},
    ];

    const chargeStatusLegend = useMemo(() => Object.entries(chargeStatusPalette).map(([status, tone]) => ({
        status,
        tone,
        label: resolveMessage(intl, statusMessageId('billingCharge', status), status),
    })), [intl]);

    const paymentDetailsSource = paymentDetails ? {...selectedCharge, ...paymentDetails} : selectedCharge;
    const paymentQrCodeImage = normalizeQrCodeImage(paymentDetails?.pixQrCodeImage);
    const selectedTargetCustomer = paymentDetailsSource?.customerId ?
        customerById.get(paymentDetailsSource.customerId) :
        null;
    const customerPhone = whatsappPhone(selectedTargetCustomer?.cellphone || selectedTargetCustomer?.phone);
    const customerEmail = selectedTargetCustomer?.notificationEmail || selectedTargetCustomer?.secondaryEmail || '';
    const paymentDetailsInstruction = paymentDetailsSource?.billingType === 'PIX' ?
        intl.formatMessage({id: 'billingPaymentInstructionsPix'}) :
        paymentDetailsSource?.billingType === 'BOLETO' ?
            intl.formatMessage({id: 'billingPaymentInstructionsBoleto'}) :
            intl.formatMessage({id: 'billingPaymentInstructionsDefault'});
    const paymentLink = paymentDetails?.paymentUrl || paymentDetails?.bankSlipUrl || paymentDetailsSource?.invoiceUrl || '';
    const paymentMessage = useMemo(() => {
        if (!paymentDetailsSource) {
            return '';
        }

        const lines = [
            `${intl.formatMessage({id: 'billingNewCharge'})}: ${paymentDetailsSource.description || intl.formatMessage({id: 'notAvailable'})}`,
            `${intl.formatMessage({id: 'sharedValue'})}: ${formatCurrency(paymentDetailsSource.value)}`,
            `${intl.formatMessage({id: 'billingDueDate'})}: ${formatDateValue(paymentDetailsSource.dueDate)}`,
        ];

        if (paymentLink) {
            lines.push(`${intl.formatMessage({id: 'billingPaymentLink'})}: ${paymentLink}`);
        }
        if (paymentDetailsSource.billingType === 'PIX' && paymentDetails?.pixCopyPaste) {
            lines.push(`${intl.formatMessage({id: 'billingPixCopyPaste'})}: ${paymentDetails.pixCopyPaste}`);
        }

        return lines.join('\n');
    }, [formatCurrency, formatDateValue, intl, paymentDetails, paymentDetailsSource, paymentLink]);

    const openWhatsappShare = () => {
        if (!customerPhone) {
            toast.warn(intl.formatMessage({id: 'billingCustomerPhoneUnavailable'}));
            return;
        }
        const url = `https://wa.me/${customerPhone}?text=${encodeURIComponent(paymentMessage)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const openEmailShare = () => {
        if (!customerEmail) {
            toast.warn(intl.formatMessage({id: 'billingCustomerEmailUnavailable'}));
            return;
        }
        const subject = `${intl.formatMessage({id: 'billingNewCharge'})} - ${paymentDetailsSource?.description || ''}`.trim();
        const url = `mailto:${encodeURIComponent(customerEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(paymentMessage)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const renderPaymentDetails = () => {
        if (loadingPaymentDetails) {
            return <HelperText><I18nMessage id="sharedLoading" /></HelperText>;
        }
        if (!paymentDetailsSource) {
            return <HelperText><I18nMessage id="billingPaymentDetailsUnavailable" /></HelperText>;
        }

        return (
            <ModalDetails>
                <DetailGrid>
                    <DetailCard>
                        <span className="detail-label"><I18nMessage id="billingTargetCustomer" /></span>
                        <div className="detail-value">{paymentDetailsSource.targetCustomerName || `#${paymentDetailsSource.customerId || 0}`}</div>
                    </DetailCard>
                    <DetailCard>
                        <span className="detail-label"><I18nMessage id="customerStatus" /></span>
                        <div className="detail-value">{resolveMessage(intl, statusMessageId('billingCharge', paymentDetailsSource.status), paymentDetailsSource.status)}</div>
                    </DetailCard>
                    <DetailCard>
                        <span className="detail-label"><I18nMessage id="billingBillingType" /></span>
                        <div className="detail-value">{paymentDetailsSource.billingType || intl.formatMessage({id: 'notAvailable'})}</div>
                    </DetailCard>
                    <DetailCard>
                        <span className="detail-label"><I18nMessage id="sharedValue" /></span>
                        <div className="detail-value">{formatCurrency(paymentDetailsSource.value)}</div>
                    </DetailCard>
                    <DetailCard>
                        <span className="detail-label"><I18nMessage id="billingDueDate" /></span>
                        <div className="detail-value">{formatDateValue(paymentDetailsSource.dueDate)}</div>
                    </DetailCard>
                    <DetailCard>
                        <span className="detail-label"><I18nMessage id="billingPaymentDate" /></span>
                        <div className="detail-value">{formatDateValue(paymentDetailsSource.paymentDate)}</div>
                    </DetailCard>
                    <DetailCard>
                        <span className="detail-label"><I18nMessage id="billingExternalChargeId" /></span>
                        <div className="detail-value">{paymentDetails?.externalChargeId || paymentDetailsSource.externalChargeId || intl.formatMessage({id: 'notAvailable'})}</div>
                    </DetailCard>
                    <DetailCard>
                        <span className="detail-label"><I18nMessage id="sharedDescription" /></span>
                        <div className="detail-value">{paymentDetailsSource.description || intl.formatMessage({id: 'notAvailable'})}</div>
                    </DetailCard>
                </DetailGrid>

                <HelperText>{paymentDetailsInstruction}</HelperText>

                <DetailCard>
                    <span className="detail-label"><I18nMessage id="billingPaymentLink" /></span>
                    <div className="detail-value">
                        {paymentDetails?.paymentUrl || paymentDetails?.bankSlipUrl || paymentDetailsSource.invoiceUrl || intl.formatMessage({id: 'billingNoPaymentLink'})}
                    </div>
                </DetailCard>

                {paymentDetailsSource.billingType === 'PIX' && (
                    <Section>
                        <SectionHeader>
                            <h3><I18nMessage id="billingPixQrCode" /></h3>
                            <PaymentActions>
                                <Button
                                    className="outline"
                                    color="theme"
                                    size="sm"
                                    onClick={copyPixCode}
                                    disabled={!paymentDetails?.pixCopyPaste}
                                >
                                    <I18nMessage id="billingPixCopyPaste" />
                                </Button>
                            </PaymentActions>
                        </SectionHeader>
                        {paymentQrCodeImage ? (
                            <QrPreview>
                                <img src={paymentQrCodeImage} alt={intl.formatMessage({id: 'billingPixQrCode'})} />
                            </QrPreview>
                        ) : (
                            <HelperText><I18nMessage id="billingNoPixQrCode" /></HelperText>
                        )}
                        {paymentDetails?.pixCopyPaste && (
                            <CodeBlock>{paymentDetails.pixCopyPaste}</CodeBlock>
                        )}
                    </Section>
                )}

                <PaymentActions>
                    <Button
                        className="outline"
                        color="success"
                        size="sm"
                        onClick={openWhatsappShare}
                        disabled={!paymentDetailsSource}
                    >
                        <I18nMessage id="billingSendByWhatsapp" />
                    </Button>
                    <Button
                        className="outline"
                        color="theme"
                        size="sm"
                        onClick={openEmailShare}
                        disabled={!paymentDetailsSource}
                    >
                        <I18nMessage id="billingSendByEmail" />
                    </Button>
                    <Button
                        className="outline"
                        color="theme"
                        size="sm"
                        onClick={copyPaymentLink}
                        disabled={!paymentDetailsSource}
                    >
                        <I18nMessage id="billingCopyPaymentLink" />
                    </Button>
                    <Button
                        className="outline"
                        color="warning"
                        size="sm"
                        onClick={() => {
                            const bankSlipUrl = paymentDetails?.bankSlipUrl || paymentDetails?.paymentUrl;
                            if (bankSlipUrl) {
                                window.open(bankSlipUrl, '_blank', 'noopener,noreferrer');
                            }
                        }}
                        disabled={paymentDetailsSource.billingType !== 'BOLETO' || !(paymentDetails?.bankSlipUrl || paymentDetails?.paymentUrl)}
                    >
                        <I18nMessage id="billingOpenBankSlip" />
                    </Button>
                    <Button
                        className="outline"
                        color="warning"
                        size="sm"
                        onClick={() => {
                            const paymentUrl = paymentDetails?.paymentUrl || paymentDetailsSource.invoiceUrl;
                            if (paymentUrl) {
                                window.open(paymentUrl, '_blank', 'noopener,noreferrer');
                            }
                        }}
                        disabled={!(paymentDetails?.paymentUrl || paymentDetailsSource.invoiceUrl)}
                    >
                        <I18nMessage id="billingOpenInvoice" />
                    </Button>
                </PaymentActions>
            </ModalDetails>
        );
    };

    const renderChargeForm = () => (
        <ModalForm onSubmit={submitCharge}>
            <Row form>
                <Col md={6}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-charge-customer"><I18nMessage id="billingTargetCustomer" /></Label>
                        <SelectField
                            id="new-charge-customer"
                            label={intl.formatMessage({id: 'billingTargetCustomer'})}
                            value={chargeForm.customerId}
                            onChange={fromValue(updateChargeField('customerId'))}
                            options={[
                                {id: '', title: intl.formatMessage({id: 'sharedSelect'})},
                                ...customerOptions.map((customer) => ({id: customer.id, title: customer.name})),
                            ]}
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-charge-billing-type"><I18nMessage id="billingBillingType" /></Label>
                        <SelectField
                            id="new-charge-billing-type"
                            label={intl.formatMessage({id: 'billingBillingType'})}
                            value={chargeForm.billingType}
                            onChange={fromValue(updateChargeField('billingType'))}
                            options={[
                                {id: 'PIX', title: 'PIX'},
                                {id: 'BOLETO', title: 'BOLETO'},
                                {id: 'UNDEFINED', title: 'UNDEFINED'},
                            ]}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row form>
                <Col md={4}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-charge-value"><I18nMessage id="sharedValue" /></Label>
                        <Input
                            id="new-charge-value"
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={chargeForm.value}
                            onChange={updateChargeField('value')}
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-charge-due-date"><I18nMessage id="billingDueDate" /></Label>
                        <Input
                            id="new-charge-due-date"
                            type="date"
                            value={chargeForm.dueDate}
                            onChange={updateChargeField('dueDate')}
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-charge-reference"><I18nMessage id="billingExternalReference" /></Label>
                        <Input
                            id="new-charge-reference"
                            value={chargeForm.externalReference}
                            onChange={updateChargeField('externalReference')}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row form>
                <Col md={12}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-charge-description"><I18nMessage id="sharedDescription" /></Label>
                        <Input
                            id="new-charge-description"
                            value={chargeForm.description}
                            onChange={updateChargeField('description')}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <div className="modal-actions">
                <Button
                    className="outline"
                    color="secondary"
                    type="button"
                    onClick={() => {
                        setChargeModalOpen(false);
                        setChargeForm(defaultChargeForm);
                    }}
                >
                    <I18nMessage id="sharedCancel" />
                </Button>
                <Button color="success" type="submit" disabled={submittingCharge || !chargeForm.customerId || !(Number(chargeForm.value) > 0) || !chargeForm.dueDate}>
                    <I18nMessage id="sharedSave" />
                </Button>
            </div>
        </ModalForm>
    );

    const renderSubscriptionForm = () => (
        <ModalForm onSubmit={submitSubscription}>
            <Row form>
                <Col md={6}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-subscription-customer"><I18nMessage id="billingTargetCustomer" /></Label>
                        <SelectField
                            id="new-subscription-customer"
                            label={intl.formatMessage({id: 'billingTargetCustomer'})}
                            value={subscriptionForm.customerId}
                            onChange={fromValue(updateSubscriptionField('customerId'))}
                            options={[
                                {id: '', title: intl.formatMessage({id: 'sharedSelect'})},
                                ...customerOptions.map((customer) => ({id: customer.id, title: customer.name})),
                            ]}
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-subscription-billing-type"><I18nMessage id="billingBillingType" /></Label>
                        <SelectField
                            id="new-subscription-billing-type"
                            label={intl.formatMessage({id: 'billingBillingType'})}
                            value={subscriptionForm.billingType}
                            onChange={fromValue(updateSubscriptionField('billingType'))}
                            options={[
                                {id: 'BOLETO', title: 'BOLETO'},
                                {id: 'PIX', title: 'PIX'},
                                {id: 'UNDEFINED', title: 'UNDEFINED'},
                            ]}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row form>
                <Col md={4}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-subscription-value"><I18nMessage id="sharedValue" /></Label>
                        <Input
                            id="new-subscription-value"
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={subscriptionForm.value}
                            onChange={updateSubscriptionField('value')}
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-subscription-due-date"><I18nMessage id="billingNextDueDate" /></Label>
                        <Input
                            id="new-subscription-due-date"
                            type="date"
                            value={subscriptionForm.nextDueDate}
                            onChange={updateSubscriptionField('nextDueDate')}
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-subscription-cycle"><I18nMessage id="billingCycle" /></Label>
                        <SelectField
                            id="new-subscription-cycle"
                            label={intl.formatMessage({id: 'billingCycle'})}
                            value={subscriptionForm.cycle}
                            onChange={fromValue(updateSubscriptionField('cycle'))}
                            options={[
                                {id: 'WEEKLY', title: resolveMessage(intl, 'billingCycleWeekly', 'WEEKLY')},
                                {id: 'BIWEEKLY', title: resolveMessage(intl, 'billingCycleBiweekly', 'BIWEEKLY')},
                                {id: 'MONTHLY', title: resolveMessage(intl, 'billingCycleMonthly', 'MONTHLY')},
                                {id: 'BIMONTHLY', title: resolveMessage(intl, 'billingCycleBimonthly', 'BIMONTHLY')},
                                {id: 'QUARTERLY', title: resolveMessage(intl, 'billingCycleQuarterly', 'QUARTERLY')},
                                {id: 'SEMIANNUALLY', title: resolveMessage(intl, 'billingCycleSemiannually', 'SEMIANNUALLY')},
                                {id: 'YEARLY', title: resolveMessage(intl, 'billingCycleYearly', 'YEARLY')},
                            ]}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row form>
                <Col md={4}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-subscription-end-date"><I18nMessage id="billingEndDate" /></Label>
                        <Input
                            id="new-subscription-end-date"
                            type="date"
                            value={subscriptionForm.endDate}
                            onChange={updateSubscriptionField('endDate')}
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-subscription-max-payments"><I18nMessage id="billingMaxPayments" /></Label>
                        <Input
                            id="new-subscription-max-payments"
                            type="number"
                            min="0"
                            step="1"
                            value={subscriptionForm.maxPayments}
                            onChange={updateSubscriptionField('maxPayments')}
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-subscription-reference"><I18nMessage id="billingExternalReference" /></Label>
                        <Input
                            id="new-subscription-reference"
                            value={subscriptionForm.externalReference}
                            onChange={updateSubscriptionField('externalReference')}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row form>
                <Col md={12}>
                    <FormGroup className="custom-formGroup">
                        <Label for="new-subscription-description"><I18nMessage id="sharedDescription" /></Label>
                        <Input
                            id="new-subscription-description"
                            value={subscriptionForm.description}
                            onChange={updateSubscriptionField('description')}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <div className="modal-actions">
                <Button
                    className="outline"
                    color="secondary"
                    type="button"
                    onClick={() => {
                        setSubscriptionModalOpen(false);
                        setSubscriptionForm(defaultSubscriptionForm);
                    }}
                >
                    <I18nMessage id="sharedCancel" />
                </Button>
                <Button color="success" type="submit" disabled={submittingSubscription || !subscriptionForm.customerId || !(Number(subscriptionForm.value) > 0) || !subscriptionForm.nextDueDate}>
                    <I18nMessage id="sharedSave" />
                </Button>
            </div>
        </ModalForm>
    );

    const filtersToggle = (
        <Button
            className="neutral filter-toggle"
            color="secondary"
            size="sm"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((current) => !current)}
        >
            <i className="fas fa-filter" /> <I18nMessage id="sharedFilters" />
        </Button>
    );

    const filtersPanel = (
        <Collapse isOpen={filtersOpen}>
            <FiltersCard>
                <CardBody>
                    <FiltersPanel>
                        <Form
                            onSubmit={(event) => {
                                event.preventDefault();
                                loadData();
                            }}
                        >
                            <Row form>
                                <Col md={3}>
                                    <FormGroup className="custom-formGroup">
                                        <Label for="billing-provider"><I18nMessage id="billingProvider" /></Label>
                                        <SelectField
                                            id="billing-provider"
                                            label={intl.formatMessage({id: 'billingProvider'})}
                                            value={filters.provider}
                                            onChange={fromValue(updateFiltersField('provider'))}
                                            options={[
                                                {id: 'asaas', title: 'Asaas'},
                                            ]}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="custom-formGroup">
                                        <Label for="billing-customer"><I18nMessage id="billingTargetCustomer" /></Label>
                                        <SelectField
                                            id="billing-customer"
                                            label={intl.formatMessage({id: 'billingTargetCustomer'})}
                                            value={filters.customerId}
                                            onChange={fromValue(updateFiltersField('customerId'))}
                                            options={[
                                                {id: '', title: intl.formatMessage({id: 'sharedAll'})},
                                                ...customerOptions.map((customer) => ({id: customer.id, title: customer.name})),
                                            ]}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={2}>
                                    <FormGroup className="custom-formGroup">
                                        <Label for="billing-paid"><I18nMessage id="billingPaid" /></Label>
                                        <SelectField
                                            id="billing-paid"
                                            label={intl.formatMessage({id: 'billingPaid'})}
                                            value={filters.paid}
                                            onChange={fromValue(updateFiltersField('paid'))}
                                            options={[
                                                {id: '', title: intl.formatMessage({id: 'sharedAll'})},
                                                {id: 'true', title: intl.formatMessage({id: 'sharedYes'})},
                                                {id: 'false', title: intl.formatMessage({id: 'sharedNo'})},
                                            ]}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={2}>
                                    <FormGroup className="custom-formGroup">
                                        <Label for="billing-overdue"><I18nMessage id="billingOverdue" /></Label>
                                        <SelectField
                                            id="billing-overdue"
                                            label={intl.formatMessage({id: 'billingOverdue'})}
                                            value={filters.overdue}
                                            onChange={fromValue(updateFiltersField('overdue'))}
                                            options={[
                                                {id: '', title: intl.formatMessage({id: 'sharedAll'})},
                                                {id: 'true', title: intl.formatMessage({id: 'sharedYes'})},
                                                {id: 'false', title: intl.formatMessage({id: 'sharedNo'})},
                                            ]}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={2}>
                                    <FormGroup className="custom-formGroup">
                                        <Label for="billing-recurring"><I18nMessage id="billingRecurring" /></Label>
                                        <SelectField
                                            id="billing-recurring"
                                            label={intl.formatMessage({id: 'billingRecurring'})}
                                            value={filters.recurring}
                                            onChange={fromValue(updateFiltersField('recurring'))}
                                            options={[
                                                {id: '', title: intl.formatMessage({id: 'sharedAll'})},
                                                {id: 'true', title: intl.formatMessage({id: 'sharedYes'})},
                                                {id: 'false', title: intl.formatMessage({id: 'sharedNo'})},
                                            ]}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row form>
                                <Col md={3}>
                                    <FormGroup className="custom-formGroup mb-0">
                                        <Label for="billing-active"><I18nMessage id="billingActiveSubscriptions" /></Label>
                                        <SelectField
                                            id="billing-active"
                                            label={intl.formatMessage({id: 'billingActiveSubscriptions'})}
                                            value={filters.active}
                                            onChange={fromValue(updateFiltersField('active'))}
                                            options={[
                                                {id: '', title: intl.formatMessage({id: 'sharedAll'})},
                                                {id: 'true', title: intl.formatMessage({id: 'sharedYes'})},
                                                {id: 'false', title: intl.formatMessage({id: 'sharedNo'})},
                                            ]}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={9} className="d-flex align-items-end justify-content-end">
                                    <div className="d-flex gap-2 flex-wrap">
                                        <Button
                                            className="neutral"
                                            color="secondary"
                                            size="sm"
                                            onClick={() => {
                                                const nextFilters = {
                                                    provider: 'asaas',
                                                    customerId: '',
                                                    paid: '',
                                                    overdue: '',
                                                    recurring: '',
                                                    active: '',
                                                };
                                                setFilters(nextFilters);
                                                loadData(nextFilters);
                                            }}
                                        >
                                            <I18nMessage id="sharedClear" />
                                        </Button>
                                        <Button className="solid" color="theme" size="sm" type="submit" disabled={loading}>
                                            <I18nMessage id="sharedUpdate" />
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </FiltersPanel>
                </CardBody>
            </FiltersCard>
        </Collapse>
    );

    return (
        <BillingBlockProvider ref={billingBlock}>
        <Layout data-testid="billing-container">
            {providerNotConfigured && (
                <Alert color="warning" role="alert" className="d-flex flex-wrap align-items-center justify-content-between mb-0">
                    <span>
                        <I18nMessage id={providerIssueMessageId[providerStatus.reason] || 'billingProviderNotConfigured'} />
                    </span>
                    <Button
                        color="warning"
                        size="sm"
                        onClick={() => history.push('/admin/manage/integrationconfig')}
                    >
                        <I18nMessage id="billingConfigureIntegration" />
                    </Button>
                </Alert>
            )}
            <ActionRow>
                {showBillingBlockTab ? (
                    <BillingBlockSettingsButton onOpen={() => changeTab('billingBlock')} />
                ) : (
                    <IconActionButton
                        className="neutral"
                        color="secondary"
                        size="sm"
                        title={intl.formatMessage({id: 'sharedIntegrationConfig'})}
                        aria-label={intl.formatMessage({id: 'sharedIntegrationConfig'})}
                        onClick={() => history.push('/admin/manage/integrationconfig')}
                    >
                        <i className="fas fa-cog" />
                    </IconActionButton>
                )}
            </ActionRow>

            <SummaryGrid>
                {summaryCards.map((card) => (
                    <SummaryCard key={card.id} data-accent={card.accent}>
                        <CardBody>
                            <span className="summary-label"><I18nMessage id={card.id} /></span>
                            <span className="summary-value">{loading ? '...' : card.value}</span>
                            {card.count && !loading && <span className="summary-count">{card.count}</span>}
                        </CardBody>
                    </SummaryCard>
                ))}
            </SummaryGrid>

            <TabShell>
                <TabsHeader>
                    <NavItem>
                        <TabsLink
                            className={classnames({active: activeTab === 'charges'})}
                            onClick={() => changeTab('charges')}
                        >
                            <I18nMessage id="billingCharges" />
                        </TabsLink>
                    </NavItem>
                    <NavItem>
                        <TabsLink
                            className={classnames({active: activeTab === 'subscriptions'})}
                            onClick={() => changeTab('subscriptions')}
                        >
                            <I18nMessage id="billingSubscriptions" />
                        </TabsLink>
                    </NavItem>
                    {showBillingBlockTab && (
                        <NavItem>
                            <TabsLink
                                className={classnames({active: activeTab === 'billingBlock'})}
                                onClick={() => changeTab('billingBlock')}
                            >
                                <I18nMessage id="billingBlockTab" />
                            </TabsLink>
                        </NavItem>
                    )}
                </TabsHeader>

                <CardBody>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="charges">
                            <Section>
                                {activeTab === 'charges' && filtersPanel}
                                <DataTable
                                    add={noop}
                                    edit={noop}
                                    remove={noop}
                                    showGroupButtons={false}
                                    keyField="id"
                                    list={mappedCharges}
                                    loading={loading}
                                    onSelectRow={setSelectedCharge}
                                    extraHeaderEl={() => (<>
                                        {filtersToggle}
                                        {!readonly && (<>
                                        <Button
                                            className="neutral"
                                            color="secondary"
                                            size="sm"
                                            onClick={copyPaymentLink}
                                            disabled={!selectedCharge || syncingCharge}
                                        >
                                            <I18nMessage id="billingCopyPaymentLink" />
                                        </Button>
                                        <Button
                                            className="neutral"
                                            color="secondary"
                                            size="sm"
                                            onClick={syncSelectedCharge}
                                            disabled={!selectedCharge || syncingCharge}
                                        >
                                            <I18nMessage id="billingSyncCharge" />
                                        </Button>
                                        <Button
                                            className="neutral"
                                            color="secondary"
                                            size="sm"
                                            onClick={reuseChargeAsDraft}
                                            disabled={!selectedCharge || syncingCharge || providerNotConfigured}
                                        >
                                            <I18nMessage id="billingReuseCharge" />
                                        </Button>
                                        </>)}
                                        <Button
                                            className="neutral"
                                            color="secondary"
                                            size="sm"
                                            onClick={openInvoice}
                                            disabled={!selectedCharge || !selectedCharge.invoiceUrl || syncingCharge}
                                        >
                                            <I18nMessage id="billingOpenInvoice" />
                                        </Button>
                                        {!readonly && (<>
                                            <Button
                                                className="neutral"
                                                color="secondary"
                                                size="sm"
                                                onClick={openPaymentDetails}
                                                disabled={!selectedCharge || syncingCharge}
                                            >
                                                <I18nMessage id="billingPaymentDetails" />
                                            </Button>
                                            <Button
                                                className="solid"
                                                color="theme"
                                                size="sm"
                                                disabled={providerNotConfigured}
                                                onClick={() => {
                                                    setChargeForm(defaultChargeForm);
                                                    setChargeModalOpen(true);
                                                }}
                                            >
                                                <i className="fas fa-plus" /> <I18nMessage id="billingNewCharge" />
                                            </Button>
                                        </>)}
                                    </>)}
                                    headers={[
                                        {title: 'billingTargetCustomer', field: 'targetCustomerName', dataSort: true},
                                        {title: 'sharedDescription', field: 'description', dataSort: true},
                                        {
                                            title: 'customerStatus',
                                            field: 'statusLabel',
                                            dataSort: true,
                                            dataFormat: (cell, row) => (
                                                <StatusPill
                                                    $background={row.statusTone.background}
                                                    $border={row.statusTone.border}
                                                    $color={row.statusTone.color}
                                                >
                                                    {cell}
                                                </StatusPill>
                                            ),
                                        },
                                        {title: 'billingBillingType', field: 'billingType', dataSort: true},
                                        {title: 'sharedValue', field: 'value', dataSort: true, dataFormat: (cell, row) => row.valueLabel},
                                        {title: 'billingDueDate', field: 'dueDate', dataSort: true, dataFormat: (cell, row) => row.dueDateLabel},
                                        {title: 'billingPaymentDate', field: 'paymentDate', dataSort: true, dataFormat: (cell, row) => row.paymentDateLabel},
                                        {title: 'billingRecurrenceType', field: 'recurrenceLabel', dataSort: true},
                                    ]}
                                />
                                <StatusLegend>
                                    {chargeStatusLegend.map((item) => (
                                        <LegendItem key={item.status}>
                                            <LegendDot color={item.tone.border} />
                                            {item.label}
                                        </LegendItem>
                                    ))}
                                </StatusLegend>
                            </Section>
                        </TabPane>

                        <TabPane tabId="subscriptions">
                            <Section>
                                {activeTab === 'subscriptions' && filtersPanel}
                                <DataTable
                                    add={noop}
                                    edit={noop}
                                    remove={noop}
                                    showGroupButtons={false}
                                    keyField="id"
                                    list={mappedSubscriptions}
                                    loading={loading}
                                    onSelectRow={setSelectedSubscription}
                                    extraHeaderEl={() => (<>
                                        {filtersToggle}
                                        {!readonly && (<>
                                        <Button
                                            className="neutral"
                                            color="secondary"
                                            size="sm"
                                            onClick={syncSelectedSubscription}
                                            disabled={!selectedSubscription || syncingSubscription}
                                        >
                                            <I18nMessage id="billingSyncSubscription" />
                                        </Button>
                                        <Button
                                            className="neutral"
                                            color="secondary"
                                            size="sm"
                                            onClick={() => setCancelDialogOpen(true)}
                                            disabled={!selectedSubscription || submittingSubscription || syncingSubscription}
                                        >
                                            <I18nMessage id="billingCancelSubscription" />
                                        </Button>
                                        <Button
                                            className="solid"
                                            color="theme"
                                            size="sm"
                                            disabled={providerNotConfigured}
                                            onClick={() => {
                                                setSubscriptionForm(defaultSubscriptionForm);
                                                setSubscriptionModalOpen(true);
                                            }}
                                        >
                                            <i className="fas fa-plus" /> <I18nMessage id="billingNewSubscription" />
                                        </Button>
                                        </>)}
                                    </>)}
                                    headers={[
                                        {title: 'billingTargetCustomer', field: 'targetCustomerName', dataSort: true},
                                        {title: 'sharedDescription', field: 'description', dataSort: true},
                                        {title: 'customerStatus', field: 'statusLabel', dataSort: true},
                                        {title: 'billingCycle', field: 'cycleLabel', dataSort: true},
                                        {title: 'sharedValue', field: 'value', dataSort: true, dataFormat: (cell, row) => row.valueLabel},
                                        {title: 'billingNextDueDate', field: 'nextDueDate', dataSort: true, dataFormat: (cell, row) => row.nextDueDateLabel},
                                    ]}
                                />
                            </Section>
                        </TabPane>

                        {showBillingBlockTab && (
                            <TabPane tabId="billingBlock">
                                {activeTab === 'billingBlock' && <BillingBlockTab readonly={readonly} />}
                            </TabPane>
                        )}
                    </TabContent>
                </CardBody>
            </TabShell>

            <Modal isOpen={chargeModalOpen} toggle={() => setChargeModalOpen((current) => !current)} size="lg">
                <ModalHeader toggle={() => setChargeModalOpen(false)}>
                    <I18nMessage id="billingNewCharge" />
                </ModalHeader>
                <ModalBody>
                    {renderChargeForm()}
                </ModalBody>
            </Modal>

            <Modal isOpen={subscriptionModalOpen} toggle={() => setSubscriptionModalOpen((current) => !current)} size="lg">
                <ModalHeader toggle={() => setSubscriptionModalOpen(false)}>
                    <I18nMessage id="billingNewSubscription" />
                </ModalHeader>
                <ModalBody>
                    {renderSubscriptionForm()}
                </ModalBody>
            </Modal>

            <CompactModal
                isOpen={paymentDetailsModalOpen}
                toggle={() => {
                    setPaymentDetailsModalOpen((current) => !current);
                    if (paymentDetailsModalOpen) {
                        setPaymentDetails(null);
                    }
                }}
                centered
            >
                <ModalHeader toggle={() => {
                    setPaymentDetailsModalOpen(false);
                    setPaymentDetails(null);
                }}>
                    <I18nMessage id="billingPaymentDetailsTitle" />
                </ModalHeader>
                <ModalBody>
                    {renderPaymentDetails()}
                </ModalBody>
            </CompactModal>

            <Dialog
                open={cancelDialogOpen}
                title={intl.formatMessage({id: 'billingCancelSubscription'})}
                description={intl.formatMessage({id: 'billingCancelSubscriptionConfirmDescription'})}
                confirmationText={intl.formatMessage({id: 'billingCancelSubscriptionConfirm'})}
                onConfirm={cancelSelectedSubscription}
                cancelationText={intl.formatMessage({id: 'sharedCancel'})}
                onCancel={() => setCancelDialogOpen(false)}
            />
        </Layout>
        </BillingBlockProvider>
    );
};

export default observer(BillingContainer);
